import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { navigate, tasksVersion } from '../store.js';
import { __ } from '../i18n.js';
import { deadlineChipLabel } from '../lib/format.js';
import { subscriptionStatusChip } from '../lib/subscriptions.js';
import { useVisibleRefresh } from '../lib/refresh.js';
import Icon from '../components/Icon.jsx';

/**
 * HH-7 dashboard card: "Waarschuwingen" — subscriptions inside their
 * switch window plus hard task deadlines the engine considers at risk.
 *
 * Nothing is judged here. Both lists arrive already decided by the server
 * (GET /dashboard/warnings → ETM_Subscriptions_Logic and ETM_Engine's own
 * warnings array), and this component only labels them. That is the WP-2
 * isDeadlineAtRisk restraint restated: the slack rule of roadmap §5 lives
 * in the engine, and a JavaScript second opinion about which deadline is
 * "at risk" is exactly the drift the rule exists to prevent.
 *
 * Hidden entirely when the fetch fails or there is nothing to warn about
 * — an empty warnings card is a card that trains you to ignore it.
 */
export default function WarningsCard() {
  const [warnings, setWarnings] = useState(null);
  const version = tasksVersion.value; // a completed task can clear a warning
  // ...and whenever the app returns to the foreground: the server froze
  // today's date into this payload when it was built.
  const visibleTick = useVisibleRefresh();

  useEffect(() => {
    let cancelled = false;

    api.dashboard
      .warnings()
      .then((res) => {
        if (!cancelled) setWarnings(res);
      })
      .catch(() => {
        if (!cancelled) setWarnings(null);
      });

    return () => {
      cancelled = true;
    };
  }, [version, visibleTick]);

  if (!warnings) return null;

  const subscriptions = warnings.subscriptions || [];
  const tasks = warnings.tasks || [];
  if (subscriptions.length === 0 && tasks.length === 0) return null;

  return (
    <section class="etm-dashboard-card etm-dashboard-card--warning">
      <div class="etm-dashboard-card__header etm-dashboard-card__header--static">
        <span class="etm-dashboard-card__icon">
          <Icon name="warning" size={20} />
        </span>
        <span class="etm-dashboard-card__title">{__('Waarschuwingen', 'etm')}</span>
      </div>

      <ul class="etm-dashboard-warnings">
        {tasks.map((task) => (
          <li key={`t${task.id}`}>
            <button type="button" class="etm-dashboard-warning" onClick={() => navigate('manage')}>
              <Icon name="schedule" size={16} />
              <span class="etm-dashboard-warning__label">{task.title}</span>
              <span class="etm-chip etm-chip--danger">{deadlineChipLabel(task.deadline)}</span>
            </button>
          </li>
        ))}
        {subscriptions.map((subscription) => {
          const chip = subscriptionStatusChip(subscription.status, subscription.weeks_until_end);
          return (
            <li key={`s${subscription.id}`}>
              <button
                type="button"
                class="etm-dashboard-warning"
                onClick={() => navigate('subscriptions')}
              >
                <Icon name="receipt_long" size={16} />
                <span class="etm-dashboard-warning__label">{subscription.name}</span>
                {chip ? <span class={`etm-chip etm-chip--${chip.tone}`}>{chip.label}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
